import { safeAwaitFunction, SafeError, SafePromise } from './safeAwait';
import { OutboundRequestPolicy } from './outboundRequestPolicy';

export type OutboundTimedRequestInit = RequestInit & { timeout?: number | null };

const safeFetchStatic = safeAwaitFunction(fetch);

const parsePositiveTimeoutEnv = (value: string | undefined): number => {
  if (value === undefined || value === '') return 0;
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : 0;
};

export const getRequestTimeoutMs = (
  init: OutboundTimedRequestInit | undefined,
  policy: OutboundRequestPolicy | undefined,
): number => {
  if (init?.timeout !== undefined && init.timeout !== null) {
    return init.timeout;
  }
  if (policy?.timeoutMs !== undefined) {
    return policy.timeoutMs;
  }
  return parsePositiveTimeoutEnv(process.env.BZ_OUTBOUND_REQUEST_TIMEOUT_MS ?? process.env.BZ_UPSTREAM_TIMEOUT_MS);
};

export const fetchWithAbortTimeout = async (
  input: RequestInfo,
  init: OutboundTimedRequestInit | undefined,
  policy: OutboundRequestPolicy | undefined,
): SafePromise<Response> => {
  const timeoutMs = getRequestTimeoutMs(init, policy);
  const fetchStartedAtMs = Date.now();
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : undefined;
  const originalSignal = init?.signal;
  let didTimeout = false;

  const timeout =
    controller && timeoutMs > 0
      ? setTimeout(() => {
          didTimeout = true;
          controller.abort();
        }, timeoutMs)
      : undefined;

  const abortFromOriginalSignal = () => controller?.abort();
  if (originalSignal && controller) {
    if (originalSignal.aborted) {
      controller.abort();
    } else {
      originalSignal.addEventListener('abort', abortFromOriginalSignal, { once: true });
    }
  }

  const result = await safeFetchStatic(input, {
    ...init,
    signal: controller?.signal ?? originalSignal,
  });

  if (timeout) clearTimeout(timeout);
  originalSignal?.removeEventListener('abort', abortFromOriginalSignal);

  if (didTimeout && result.err) {
    return {
      err: new SafeError('outbound request timed out', 'timeout', {
        fetchDurationMs: Date.now() - fetchStartedAtMs,
        outboundRequestPolicy: policy?.name,
        status: 504,
        timeoutBudgetMs: timeoutMs,
      }),
    };
  }

  return result;
};
